import { defined } from '../defined';
import { errorMessages } from '../../constants';

const errorIntro = 'Could not interpolate string';

type MessageValue = string | Record<string, unknown> | undefined;
type MessageCollection = Record<string, MessageValue>;
type InterpolationArgs = string[] | undefined;

export const interpolationErrors = {
  missingArgs: `${errorIntro}, arguments missing`,
};

const getMessageValue = (messages: MessageCollection, messageKey: string): MessageValue => {
  return messageKey.split('.').reduce<MessageValue>((currentValue, key) => {
    if (typeof currentValue === 'string' || currentValue === undefined) {
      return undefined;
    }

    return currentValue[key] as MessageValue;
  }, messages);
};

const interpolate = (message: string | undefined, args: InterpolationArgs): string | undefined => {
  if (typeof message !== 'string') {
    return message;
  }

  const argument = /\{\d+\}/.exec(message);
  if (argument) {
    const argumentNumber = Number.parseInt(argument[0].replace(/\D/g, ''), 10);
    if (!defined('array', args)) {
      throw new Error(interpolationErrors.missingArgs);
    }
    const interpolationArgs = args as string[];
    if (interpolationArgs.length <= argumentNumber) {
      throw new Error(interpolationErrors.missingArgs);
    }
    const updatedMessage = message.replace(argument[0], interpolationArgs[argumentNumber]);
    return interpolate(updatedMessage, interpolationArgs);
  }
  return message;
};

// Allows caller to specify one or more arguments for a dynamic message using C#-style syntax
// e.g. 'This is a {0} error message thrown by {1}'. The desired values for {0} and {1} should be
// specified in an array and passed as args, e.g. ['friendly', 'Product Movement']
// The returned value would then be 'This is a friendly error message thrown by Product Movement'
const getDynamicString = (
  messageKey: string,
  args?: string[],
  messages: MessageCollection = errorMessages as MessageCollection
): string | undefined => {
  let message = getMessageValue(messages, messageKey);
  message = typeof message === 'string' ? message : undefined;
  return interpolate(message, args);
};

export default getDynamicString;
